"use client";

import { isToday } from "date-fns";
import { Plus } from "lucide-react";
import { useMemo } from "react";

import { cn } from "@/lib/utils";

import { useCalendarContext } from "../calendar-context";
import { type DisabledSpan, layoutDayEvents } from "../event-layout";
import { DISABLED_HATCH } from "../styles";
import { type CalendarEvent } from "../types";
import { atMinutes, formatDayLabel, formatTime } from "../utils";

import { EventBlock } from "./event-block";
import { NowLine } from "./now-indicator";

// =============================================================================

// Very short events still get a readable, clickable height
const MIN_EVENT_MINUTES = 20;

type WeekDayColumnProps = {
  day: Date;
  dayKey: string;
  /** Timed events of the day (multi-day ones render as day segments) */
  events: CalendarEvent[];
  disabledSpans: DisabledSpan[];
  /** Start minutes of the clickable slots (empty without `onSlotClick`) */
  freeSlots: number[];
  /** Slot holding the grid's single tab stop, if it is in this column */
  tabStop: number | null;
  onSlotFocus: (dayKey: string, minutes: number) => void;
  className?: string;
};

/**
 * One day of the time grid: hour lines, dimmed off-hours, hatched disabled
 * spans, the free slot buttons and the positioned events.
 */
export function WeekDayColumn({
  day,
  dayKey,
  events,
  disabledSpans,
  freeSlots,
  tabStop,
  onSlotFocus,
  className,
}: WeekDayColumnProps) {
  const {
    locale,
    labels,
    minHour,
    maxHour,
    slotDuration,
    businessHours,
    onSlotClick,
  } = useCalendarContext();

  const gridStart = minHour * 60;
  const totalMinutes = (maxHour - minHour) * 60;
  const toGeometry = (startMin: number, endMin: number) => ({
    top: `${((startMin - gridStart) / totalMinutes) * 100}%`,
    height: `${((endMin - startMin) / totalMinutes) * 100}%`,
  });

  const positioned = useMemo(
    () =>
      layoutDayEvents(
        day,
        events,
        minHour,
        maxHour,
        Math.max(slotDuration, MIN_EVENT_MINUTES),
        businessHours
      ),
    [day, events, minHour, maxHour, slotDuration, businessHours]
  );

  const hours = Array.from(
    { length: maxHour - minHour },
    (_, i) => minHour + i
  );

  return (
    <div
      role="group"
      aria-label={formatDayLabel(day, locale)}
      className={cn("relative", className)}
    >
      {hours.map((hour, i) => {
        const isOffHours =
          !!businessHours &&
          (hour < businessHours.startHour || hour >= businessHours.endHour);
        return (
          <div
            key={hour}
            aria-hidden
            className={cn(
              "relative h-(--calendar-hour-height)",
              i > 0 && "border-t border-line",
              isOffHours && "bg-surface-2/60"
            )}
          >
            {slotDuration < 60 && (
              <div className="absolute inset-x-0 top-1/2 border-t border-dashed border-line/70" />
            )}
          </div>
        );
      })}

      {disabledSpans.map((span) => (
        <div
          key={span.startMin}
          aria-hidden
          className={cn(
            "absolute inset-x-0 cursor-not-allowed",
            DISABLED_HATCH
          )}
          style={toGeometry(span.startMin, span.endMin)}
        />
      ))}

      {onSlotClick &&
        freeSlots.map((minutes) => {
          const start = atMinutes(day, minutes);
          const geometry = toGeometry(minutes, minutes + slotDuration);
          return (
            <button
              key={minutes}
              type="button"
              aria-label={labels.slot(start)}
              data-slot-day={dayKey}
              data-slot-min={minutes}
              tabIndex={tabStop === minutes ? 0 : -1}
              onFocus={() => onSlotFocus(dayKey, minutes)}
              onClick={() =>
                onSlotClick({
                  start,
                  end: atMinutes(day, minutes + slotDuration),
                })
              }
              className="group/slot absolute inset-x-1 z-1 flex scroll-mt-24 scroll-ml-16 items-center gap-1 overflow-hidden rounded-md border border-foreground/10 bg-surface-1 px-1.5 text-muted-foreground transition-colors duration-200 outline-none hover:border-foreground hover:bg-foreground hover:text-background focus-visible:border-foreground focus-visible:bg-foreground focus-visible:text-background focus-visible:ring-2 focus-visible:ring-ring/40"
              // 1px gap between consecutive slots
              style={{
                top: `calc(${geometry.top} + 1px)`,
                height: `calc(${geometry.height} - 2px)`,
              }}
            >
              {/* Free slots show their time; the "+" appears on hover */}
              <span className="flex items-center gap-1">
                <Plus
                  aria-hidden
                  className="-ml-4 size-3.5 shrink-0 opacity-0 transition-[margin,opacity] duration-200 group-hover/slot:ml-0 group-hover/slot:opacity-100 group-focus-visible/slot:ml-0 group-focus-visible/slot:opacity-100"
                />
                <span className="text-xs leading-none font-medium tabular-nums">
                  {formatTime(start, locale)}
                </span>
              </span>
            </button>
          );
        })}

      {positioned.map((item) => (
        <EventBlock
          key={item.event.id}
          positioned={item}
          totalMinutes={totalMinutes}
        />
      ))}

      {isToday(day) && <NowLine />}
    </div>
  );
}
